/**
 * CONSENT-DEVICE-1 — WHAT SHE AGREED TO, identified by its own words.
 *
 * COMMAND C-31 item 4 settles the version question:
 *   doc_id  = the i18n key of the consent body (record.consentBody)
 *   locale  = the locale it was shown in
 *   version = sha256 = SHA-256 of the EXACT RENDERED STRING she was shown
 *
 * The ledger registers each unseen (doc_id, locale, sha256) with its full text on first sight, under
 * source "client_rendered". So an auditor reads the exact wording a teacher saw, and ANY wording
 * change — a translation edit included — produces a new version and asks again. That is the point
 * rather than a cost: new words are new consent.
 *
 * WHY THE RENDERED STRING AND NOT THE KEY. A key is stable while its text changes, so a key-based
 * version would let the wording move under a teacher who is never asked again. Hashing what was on
 * screen is the only identifier that cannot drift away from what she actually read.
 */

export const CONSENT_TYPE = "camera_microphone";

/** The consent body's key IS its document id, by ruling. One place, so the two cannot disagree. */
export const CONSENT_DOC_ID = "record.consentBody";

/**
 * SHA-256 of a string, hex, lowercase.
 *
 * Returns null when the platform cannot hash — `crypto.subtle` is absent in insecure contexts (plain
 * http) and in some engines. NULL IS NOT A HASH AND MUST NOT BE TREATED AS ONE: a caller that cannot
 * identify the document cannot know whether she has already agreed to THIS wording, so the honest
 * response is to ask her again. `consentDocFor` and the gate below both take that direction.
 */
export async function sha256Hex(text) {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle || typeof TextEncoder === "undefined") return null;
  try {
    const digest = await subtle.digest("SHA-256", new TextEncoder().encode(String(text)));
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

/**
 * The document descriptor for the consent text as it was actually rendered.
 *
 * @param {string} renderedText  the exact string on screen — not the key, not the default
 * @param {string} locale        the active language
 * @returns {Promise<{consent_type, doc_id, locale, version, sha256}|null>} null when unhashable
 */
export async function consentDocFor(renderedText, locale) {
  const text = String(renderedText || "");
  // An empty body is not a document. Rendering nothing and recording consent to it would be a lie
  // in the ledger, which is worse than asking again.
  if (!text.trim()) return null;

  const hash = await sha256Hex(text);
  if (!hash) return null;

  return {
    consent_type: CONSENT_TYPE,
    doc_id: CONSENT_DOC_ID,
    locale: String(locale || "en"),
    version: hash,
    sha256: hash,
    // The full text travels on first sight so the ledger can register wording it has not seen. The
    // server keys on (doc_id, locale, sha256) and ignores it thereafter.
    text,
    source: "client_rendered",
  };
}

export default consentDocFor;
